import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { CourseCard, Typo } from '../../ui';
import { useSelector } from 'react-redux';
import { selectCurrentStoreHideVat, selectStoreName, selectUserTheme } from '../../features/webshop/globalSlice';
import { media } from '../../utils/media';
import { useNavigate } from 'react-router-dom';
// import { useTranslation } from 'react-i18next';
import { useFeatures } from '../../features/settings/hooks';
import ListCoursesSkeleton from './ListCoursesSkeleton';

//revisit it:
const AllCoursesWrapper = styled.div`
  width: 100%;
  /* Grid Fallback */
  display: flex;
  flex-wrap: wrap;

  /* Supports Grid */
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-gap: 24px;

  ${media.lessThan('tablet')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.lessThan('phablet')} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const ListCourses = ({
  items,
  addItem,
  refCourses,
  module,
  showProgress,
  showButtons,
  showPackageInfo,
  CardComponent,
  categories,
  isLoading = false, // New prop for loading state
  isUpdating = false,
  skeletonCount = 3,
  owner,
  emptyMessage,
}) => {
  const navigate = useNavigate();
  const storeSlug = useSelector(selectStoreName);
  const storeTheme = useSelector(selectUserTheme);
  const hideVAT = useSelector(selectCurrentStoreHideVat);
  const features = useFeatures();

  const [courses, setCourses] = useState([]);

  useEffect(() => {
    if (!items) {
      setCourses(items);
      return;
    }

    const sortPackagesFirst = (list) =>
      [...list].sort((a, b) => {
        const aIsPackage = a.type === 2 ? 1 : 0;
        const bIsPackage = b.type === 2 ? 1 : 0;
        if (bIsPackage !== aIsPackage) return bIsPackage - aIsPackage;
        return (a.title || '').localeCompare(b.title || '');
      });

    if (categories?.length) {
      const normalizedCategories = categories.map((cat) => (typeof cat === 'string' ? cat : cat?.slug)).filter(Boolean);

      const coursesList = items
        ?.map((course) => {
          const courseCategorySlugs = course?.categories?.map((cat) => (typeof cat === 'string' ? cat : cat?.slug)).filter(Boolean) || [];
          const matchesCategory = courseCategorySlugs.some((slug) => normalizedCategories.includes(slug));
          return matchesCategory ? course : null;
        })
        .filter(Boolean);

      setCourses(sortPackagesFirst(coursesList));
    } else {
      setCourses(sortPackagesFirst(items));
    }
  }, [items, categories]);

  const goToCourse = useCallback(
    (item, isBundle) => {
      if (module === 'dashboard') {
        navigate(`/view/course/${item.id}`);
      } else navigate(`/store/${storeSlug}/${isBundle ? 'package/' : ''}${item.slug}`, { state: item });
    },
    [navigate, module, storeSlug],
  );

  const goToEdit = useCallback(
    (item) => {
      if (features?.modernCourseBuilder) {
        // Modern route: ID-based
        navigate(`/edit/course/${item.id}`);
      } else {
        // Legacy route: slug-based
        navigate(`/course/${item.ownerSlug}/${item.slug}`, { state: item });
      }
    },
    [navigate, features],
  );

  const goToCourseSettings = useCallback(
    (item) => {
      navigate(`/courses/${item.id}/settings`);
    },
    [navigate],
  );

  const ComponentCourseCard = CardComponent || CourseCard;

  // Show skeleton loader when in loading state
  if (isLoading) {
    return <ListCoursesSkeleton count={skeletonCount} showProgress={showProgress} showButtons={showButtons} />;
  }

  return (
    <>
      {courses?.length > 1 ? (
        <AllCoursesWrapper ref={refCourses}>
          {courses.map((item, index) => (
            <ComponentCourseCard
              key={`course-card${item.id}-${index}`}
              courseInfo={item}
              goToCoursePage={goToCourse}
              goToSettings={goToCourseSettings}
              addToCart={addItem}
              goToEditCourse={goToEdit}
              showButtons={showButtons}
              showProgress={showProgress}
              inGrid={true}
              showPackageInfo={showPackageInfo}
              storeTheme={storeTheme}
              hideVAT={hideVAT}
              draftCourses={features.draftCourses}
              isUpdating={isUpdating}
              ownerName={owner?.name}
            />
          ))}
        </AllCoursesWrapper>
      ) : !!courses?.length ? (
        <ComponentCourseCard
          key={`course-card${courses[0]?.id}`}
          courseInfo={courses[0]}
          goToCoursePage={goToCourse}
          goToEditCourse={goToEdit}
          addToCart={addItem}
          goToSettings={goToCourseSettings}
          showButtons={showButtons}
          showProgress={showProgress}
          showPackageInfo={showPackageInfo}
          ownerAvatar={storeTheme.logoUrl}
          ownerColor={storeTheme.color}
          storeTheme={storeTheme}
          hideVAT={hideVAT}
          draftCourses={features.draftCourses}
          isUpdating={isUpdating}
          ownerName={owner?.name}
        />
      ) : emptyMessage ? (
        <Typo.Text>{emptyMessage}</Typo.Text>
      ) : null}
    </>
  );
};

export default ListCourses;
